import React from 'react'
import { Edit2, Trash2, Eye } from 'lucide-react'
import { formatPrice } from './formatPrice'
import { formatEnumString } from './stringHelpers'

// Status badge renderer
export const renderStatusBadge = (status: string, options?: { active?: boolean }) => {
  if (!status) return null

  const statusMap: Record<string, { bg: string; text: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-800' },
    inactive: { bg: 'bg-red-100', text: 'text-red-800' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    processing: { bg: 'bg-indigo-100', text: 'text-indigo-800' },
    confirmed: { bg: 'bg-blue-100', text: 'text-blue-800' },
    completed: { bg: 'bg-green-100', text: 'text-green-800' },
    failed: { bg: 'bg-red-100', text: 'text-red-800' },
    shipped: { bg: 'bg-purple-100', text: 'text-purple-800' },
    out_for_delivery: { bg: 'bg-amber-100', text: 'text-amber-800' },
    delivered: { bg: 'bg-green-100', text: 'text-green-800' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
    returned: { bg: 'bg-rose-100', text: 'text-rose-800' },
    paid: { bg: 'bg-green-100', text: 'text-green-800' },
    unpaid: { bg: 'bg-red-100', text: 'text-red-800' },
    refunded: { bg: 'bg-gray-100', text: 'text-gray-800' },
    pickup_pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    pickup_assigned: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    pickup_scheduled: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    pickup_completed: { bg: 'bg-green-100', text: 'text-green-800' },
  }

  const cleanKey = status.toLowerCase().replace(/[\s-]/g, '_')
  const config = statusMap[cleanKey] || { bg: 'bg-gray-100', text: 'text-gray-800' }

  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
      {formatEnumString(status)}
    </span>
  )
}

// Price formatter
export const renderPrice = (value: any) => formatPrice(value)

// Date formatter
export const renderDate = (date: string | Date) => {
  const d = new Date(date)
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
  return d.toLocaleDateString('en-US', options)
}

// Truncate text
export const renderTruncated = (text: string, maxLength: number = 30) => {
  if (!text) return '-'
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

// Action buttons
export interface ActionButtonsProps {
  onView?: () => void
  onEdit?: () => void
  onDelete?: () => void
  viewLabel?: string
  editLabel?: string
  deleteLabel?: string
}

export const renderActionButtons = ({
  onView,
  onEdit,
  onDelete,
  viewLabel = 'View',
  editLabel = 'Edit',
  deleteLabel = 'Delete',
}: ActionButtonsProps) => (
  <div className="flex gap-3">
    {onView && (
      <button
        onClick={onView}
        className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
        title={viewLabel}
      >
        <Eye size={16} />
      </button>
    )}
    {onEdit && (
      <button
        onClick={onEdit}
        className="text-[#6D3A0D] hover:text-[#5D2A0D] font-semibold"
        title={editLabel}
      >
        <Edit2 size={16} />
      </button>
    )}
    {onDelete && (
      <button
        onClick={onDelete}
        className="text-red-600 hover:text-red-700 font-semibold"
        title={deleteLabel}
      >
        <Trash2 size={16} />
      </button>
    )}
  </div>
)

// Toggle button
export interface ToggleButtonProps {
  isActive: boolean
  onToggle: () => void
  activeLabel?: string
  inactiveLabel?: string
}

export const renderToggleButton = ({
  isActive,
  onToggle,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
}: ToggleButtonProps) => (
  <button
    onClick={onToggle}
    className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
      isActive ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'
    }`}
  >
    {isActive ? activeLabel : inactiveLabel}
  </button>
)

// Stock status with low stock indicator
export const renderStockStatus = (stock: number, lowStockThreshold: number = 5) => (
  <div className="flex items-center gap-2">
    <span>{stock} units</span>
    {stock <= lowStockThreshold && <span className="text-red-600 text-xs font-semibold">Low!</span>}
  </div>
)

// Rating renderer
export const renderRating = (rating: number, reviewCount?: number) => (
  <div className="flex items-center gap-2">
    <span className="font-semibold">{rating.toFixed(1)}</span>
    {reviewCount !== undefined && <span className="text-xs text-gray-500">({reviewCount})</span>}
  </div>
)

// Tags renderer
export const renderTags = (tags: string[]) => (
  <div className="flex flex-wrap gap-2">
    {tags.map((tag, idx) => (
      <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
        {tag}
      </span>
    ))}
  </div>
)
